import React from 'react';
import {Box, Dialog, Typography} from '@mui/material';
import {WarningAmberRounded} from '@mui/icons-material';
import {useDelete, useNotify, useRedirect, useTranslate} from 'react-admin';
import {t} from '../../themes/statamicTokens';
import PrimaryButton from './PrimaryButton';

const DeleteConfirmDialog = ({
    open,
    onClose,
    resource,
    record,
    recordLabel,
    redirectTo = 'list',
    message,
    onSuccess,
}) => {
    const [deleteOne, {isLoading}] = useDelete();
    const notify = useNotify();
    const redirect = useRedirect();
    const translate = useTranslate();

    const handleDelete = () => {
        deleteOne(
            resource,
            {id: record.id, previousData: record},
            {
                onSuccess: () => {
                    notify('ra.notification.deleted', {type: 'info', messageArgs: {smart_count: 1}});
                    onClose();
                    if (onSuccess) onSuccess();
                    else redirect(redirectTo, resource);
                },
                onError: (error) => {
                    notify('ra.notification.http_error', {
                        type: 'error',
                        messageArgs: {_: error?.message || 'unknown error'},
                    });
                },
            }
        );
    };

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: `${t.radius.lg}px`,
                        border: `1px solid ${t.color.border.default}`,
                        boxShadow: t.shadow.modal,
                        maxWidth: 440,
                    },
                },
            }}
        >
            <Box sx={{p: 3}}>
                <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start'}}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            backgroundColor: t.color.status.dangerTint,
                            color: t.color.status.dangerSolid,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <WarningAmberRounded sx={{fontSize: 20}}/>
                    </Box>
                    <Box sx={{minWidth: 0, flex: 1}}>
                        <Typography
                            sx={{
                                fontSize: `${t.type.h3.size}px`,
                                fontWeight: t.type.h3.weight,
                                color: t.color.text.primary,
                                lineHeight: 1.3,
                            }}
                        >
                            {translate('Delete')} {recordLabel || translate('this record')}?
                        </Typography>
                        <Typography
                            sx={{
                                mt: 1,
                                fontSize: `${t.type.small.size}px`,
                                color: t.color.text.secondary,
                                lineHeight: 1.5,
                            }}
                        >
                            {message || translate('This action cannot be undone. The record will be permanently removed.')}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3}}>
                    <PrimaryButton variant="secondary" onClick={onClose} disabled={isLoading}>
                        {translate('ra.action.cancel')}
                    </PrimaryButton>
                    <PrimaryButton
                        onClick={handleDelete}
                        disabled={isLoading}
                        sx={{
                            backgroundColor: t.color.status.dangerSolid,
                            borderColor: t.color.status.dangerSolid,
                            color: t.color.text.inverse,
                            '&:hover': {
                                backgroundColor: '#DC2626',
                                borderColor: '#DC2626',
                            },
                        }}
                    >
                        {isLoading ? `${translate('ra.action.delete')}…` : translate('ra.action.delete')}
                    </PrimaryButton>
                </Box>
            </Box>
        </Dialog>
    );
};

export default DeleteConfirmDialog;
